import React from 'react';
import { createPortal } from 'react-dom';
import { useAccessibility } from '@/context/AccessibilityContext';
import { usePublicTheme } from '@/context/PublicThemeContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  Accessibility, Type, Contrast, Sparkles, Underline, MousePointer2,
  AlignJustify, RotateCcw, Minus, Plus,
} from 'lucide-react';

function ToggleRow({ icon: Icon, label, desc, checked, onChange, testid }) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-accent">
      <span className="flex items-start gap-2.5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          <span className="block text-sm font-medium leading-none">{label}</span>
          {desc && <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>}
        </span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

export function AccessibilityWidget() {
  const a11y = useAccessibility();
  const themeCtx = usePublicTheme();
  if (!a11y || typeof document === 'undefined') return null;
  const { settings, toggle, incFont, decFont, reset, isDefault } = a11y;

  // Scope tema dedicato, montato su <body> tramite portal: così il filtro
  // "Alto contrasto" (applicato a .sila-public) NON ancora più il pulsante
  // fixed a quel contenitore facendolo sparire fuori dallo schermo.
  const scopeCls = cn('a11y-scope', themeCtx?.theme === 'dark' && 'dark');

  return createPortal(
    <div className={scopeCls}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Opzioni di accessibilità"
            data-testid="a11y-fab"
            className="fixed bottom-24 left-4 z-[9997] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_6px_16px_rgba(0,0,0,0.15)] outline-none transition-transform duration-200 hover:scale-105 focus-visible:ring-4 focus-visible:ring-primary/40 sm:bottom-5 sm:left-5 sm:h-14 sm:w-14"
          >
            <Accessibility className="h-6 w-6" />
            {!isDefault && <span className="absolute right-0 top-0 h-3.5 w-3.5 rounded-full border-2 border-background bg-[hsl(var(--brand-sun))]" aria-hidden />}
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" sideOffset={12} className={cn('z-[9998] w-80 p-3', scopeCls)} data-testid="a11y-panel">
          <div className="mb-2 flex items-center gap-2 px-1">
            <Accessibility className="h-5 w-5 text-primary" />
            <h3 className="font-display text-base font-semibold">Accessibilità</h3>
          </div>

          {/* Dimensione testo */}
          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <span className="flex items-center gap-2 text-sm font-medium"><Type className="h-4 w-4 text-primary" />Dimensione testo</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-7 w-7 rounded-full" onClick={decFont} disabled={settings.fontScale <= 0.9} aria-label="Riduci testo" data-testid="a11y-font-dec"><Minus className="h-4 w-4" /></Button>
              <span className="w-10 text-center text-sm font-semibold tabular-nums" data-testid="a11y-font-value">{Math.round(settings.fontScale * 100)}%</span>
              <Button variant="outline" size="icon" className="h-7 w-7 rounded-full" onClick={incFont} disabled={settings.fontScale >= 1.4} aria-label="Aumenta testo" data-testid="a11y-font-inc"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          <Separator className="my-2" />

          <div className="space-y-0.5">
            <ToggleRow icon={Contrast} label="Alto contrasto" desc="Colori più marcati e leggibili" checked={settings.highContrast} onChange={() => toggle('highContrast')} testid="a11y-contrast" />
            <ToggleRow icon={Sparkles} label="Riduci animazioni" desc="Meno movimento e transizioni" checked={settings.reduceMotion} onChange={() => toggle('reduceMotion')} testid="a11y-reduce-motion" />
            <ToggleRow icon={Type} label="Font per dislessia" desc="Carattere più leggibile" checked={settings.dyslexiaFont} onChange={() => toggle('dyslexiaFont')} testid="a11y-dyslexia" />
            <ToggleRow icon={Underline} label="Sottolinea i link" desc="Evidenzia i collegamenti" checked={settings.underlineLinks} onChange={() => toggle('underlineLinks')} testid="a11y-underline" />
            <ToggleRow icon={MousePointer2} label="Cursore grande" desc="Puntatore più visibile" checked={settings.bigCursor} onChange={() => toggle('bigCursor')} testid="a11y-big-cursor" />
            <ToggleRow icon={AlignJustify} label="Guida di lettura" desc="Barra che segue il cursore" checked={settings.readingGuide} onChange={() => toggle('readingGuide')} testid="a11y-reading-guide" />
          </div>

          <Separator className="my-2" />

          <Button variant="ghost" className="w-full justify-center gap-2 text-muted-foreground" onClick={reset} disabled={isDefault} data-testid="a11y-reset">
            <RotateCcw className="h-4 w-4" />Ripristina impostazioni
          </Button>
        </PopoverContent>
      </Popover>
    </div>,
    document.body,
  );
}

export default AccessibilityWidget;
